import React, { useState, useCallback } from 'react';
import { Table, Pagination, Button, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

// Tradução dos códigos de status (sts)
const statusMessages = {
  0: 'Inicializando',
  1: 'Abrindo tampa',
  2: 'Fechando tampa',
  3: 'Leitura vazio',
  4: 'Iniciando dosagem',
  5: 'Dosando amostra',
  6: 'Desligando rosca',
  7: 'Leitura cheio/envio',
  96: 'Liberado para configuração',
  97: 'Necessita reinicialização',
  98: 'Standby',
  99: 'Acordando do standby'
};

// Tradução dos códigos de erro (err)
const errorMessages = {
  0: 'Sem erro',
  1: 'Tampa não fechou totalmente',
  2: 'Erro de umidade (cheio)',
  3: 'Sensor de cheio acionado',
  4: 'Timeout enchendo',
  5: 'Tampa não movimentou abrindo',
  6: 'Tampa não movimentou fechando',
  7: 'Sensor de vazio acionado',
  8: 'Sensor de cheio na descarga'
};

const Results = ({ data, permission, currentTheme }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(''); // Nova state para coluna de ordenação
  const [orderDirection, setOrderDirection] = useState('asc'); // Nova state para direção da ordenação

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page ao mudar linhas por página
  }, []);

  // Função para manipular o clique no cabeçalho e ordenar
  const handleSort = (column) => {
    if (orderBy === column) {
      // Inverte a direção se a coluna já está selecionada
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Define uma nova coluna e inicia com ascendente
      setOrderBy(column);
      setOrderDirection('asc');
    }
    // Resetar para primeira página quando mudar ordenação
    setPage(0);
  };

  // Função para ordenar os dados com base na coluna e direção
  const sortData = (dataToSort) => {
    if (!orderBy) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let valueA, valueB;

      // Definir valores para comparação com base na coluna
      switch (orderBy) {
        case 'serial':
          valueA = a?.serial || a?.serialnumber || '';
          valueB = b?.serial || b?.serialnumber || '';
          break;
        case 'sts':
          valueA = a?.sts ?? 0;
          valueB = b?.sts ?? 0;
          break;
        case 'err':
          valueA = a?.err ?? 0;
          valueB = b?.err ?? 0;
          break;
        case 'lastUpdate':
          valueA = a?.lastUpdate || 0;
          valueB = b?.lastUpdate || 0;
          break;
        case 'ip':
          valueA = a?.ip || '';
          valueB = b?.ip || '';
          break;
        case 'company_id':
          valueA = a?.company_id || '';
          valueB = b?.company_id || '';
          break;
        case 'company_name':
          valueA = a?.company?.name || '';
          valueB = b?.company?.name || '';
          break;
        case 'updatedAt':
          valueA = new Date(a?.updatedAt || 0);
          valueB = new Date(b?.updatedAt || 0);
          break;
        default:
          valueA = '';
          valueB = '';
      }

      // Comparação dos valores considerando a direção
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else if (valueA instanceof Date && valueB instanceof Date) {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        const comparison = String(valueA).localeCompare(String(valueB));
        return orderDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  // Renderizar ícone de ordenação usando react-icons
  const renderSortIcon = (column) => {
    if (orderBy !== column) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return orderDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  // Aplicar ordenação antes da paginação
  const sortedData = sortData(data);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleClick = (_id) => {
    history.push(`/support-iot/${_id}`);
  };

  // Função para formatar datas
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    // Aceita tanto string ISO quanto timestamp numérico
    const date = typeof dateString === 'number' ? new Date(dateString) : new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Equipamentos
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className="text-center" onClick={() => handleSort('serial')} style={{ cursor: 'pointer' }}>
                  SERIAL {renderSortIcon('serial')}
                </th>
                <th className="text-center">EMPRESA</th>
                <th className="text-center">OBSERVAÇÃO</th>
                <th className="text-center" onClick={() => handleSort('sts')} style={{ cursor: 'pointer' }}>
                  STATUS {renderSortIcon('sts')}
                </th>
                <th className="text-center" onClick={() => handleSort('err')} style={{ cursor: 'pointer' }}>
                  ERRO {renderSortIcon('err')}
                </th>
                <th className="text-center" onClick={() => handleSort('lastUpdate')} style={{ cursor: 'pointer' }}>
                  ÚLTIMA ATUALIZAÇÃO {renderSortIcon('lastUpdate')}
                </th>
                <th className="text-center">AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="clickable-row"
                  style={{ cursor: permission?.read ? 'pointer' : 'default' }}
                >
                  <td className="text-center pt-2">{item.serial || item.serialnumber || '--'}</td>
                  <td className="text-center pt-2">{item.company?.name || item.company || '--'}</td>
                  <td className="text-center pt-2">{item.observation || '--'}</td>
                  <td className="text-center pt-2">
                    {item.sts !== undefined ? (
                      <span title={`Código: ${item.sts}`}>
                        {statusMessages[item.sts] || `Desconhecido (${item.sts})`}
                      </span>
                    ) : '--'}
                  </td>
                  <td className="text-center pt-2">
                    {item.err !== undefined ? (
                      <span 
                        style={{ color: item.err > 0 ? 'red' : 'inherit', fontWeight: item.err > 0 ? 'bold' : 'normal' }}
                        title={`Código: ${item.err}`}
                      >
                        {errorMessages[item.err] || `Desconhecido (${item.err})`}
                      </span>
                    ) : '--'}
                  </td>
                  <td className="text-center pt-2">
                    {item.lastUpdate ? formatDate(item.lastUpdate) : '--'}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(item?.serial || item?.serialnumber)
                      }}
                      disabled={!permission?.write}
                    >
                      Conectar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"
            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"
            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
